import React, { useRef } from 'react';
import { useSelector } from 'react-redux';
import { cx } from 'linaria';
import { counterSticky } from 'common/components/product-price-view/sticky.styles';
import { cn, jcm } from 'utils';
import {
  getDisplayedName,
  getProductImages,
  getProductId,
  getPrimaryPrice,
  getProductPriceCategory,
  isWebSellable,
} from 'features/product-info/ducks/selectors';
import { ImagePlaceholder } from '@navi-toolbox/ui-components';
import { styles } from '../pdp-showcase/styles';
import { PdpPriceView } from '../pdp-price-view';
import { PdpAddToBasket } from '../pdp-add-to-basket';
import { mediaMobile } from '../pdp-media-gallery/styles';
import { PdpSelectTintColor } from '../pdp-select-tint-color';
import { useOptimisedImages } from '@navi-toolbox/hooks';
import { PushPromoBanner } from 'common/components/push-promo-banner';
import { getShowCatchPushPromo, getYaSplitInfo } from 'features/page-model';
import { getCartItem } from 'features/basket';
import {
  fixedOuterContainerStyle,
  innerContainer,
  innerContainerFixed,
  thumbImg,
  unfixedOuterContainerStyleStyle,
  Basket,
  ToCart,
  pushPromoBanner,
} from './pdp-sticky-card-panel.styles';
import { PriceAndAddToBasketProps } from './types';
import { useTranslation } from 'react-i18next';
import { StickyCardPanelSkeleton } from './sticky-card-panel-skeleton';
import { YandexSplitBanner } from 'features/yandex-split-banner';
import { useAddBodyPadding, useStickyLogic } from './hooks';

export const StickyCardPanel: React.FC<PriceAndAddToBasketProps> = ({
  productDataIsLoading,
  shouldShowTintService,
  outOfStockPriceDisplayed,
}) => {
  const { t } = useTranslation();
  const containerRef = useRef<HTMLDivElement | null>(null);
  const menuMobileRef = useRef<HTMLDivElement>(null);
  const photoSrc = useSelector(getProductImages)?.[0].url ?? '';
  const productName = useSelector(getDisplayedName) ?? '';
  const { img } = useOptimisedImages({
    src: photoSrc,
    sizes: {
      img: {
        w: '40',
        h: '40',
      },
    },
  });
  const isSellable = useSelector(isWebSellable);
  const productId = useSelector(getProductId);
  const { price, prevPrice, discountPercent } = useSelector(getPrimaryPrice) || {};
  const priceCategory = useSelector(getProductPriceCategory);
  const showCatchPushPromo = useSelector(getShowCatchPushPromo);
  const cartEntity = useSelector(getCartItem(productId || ''));
  const inBasket = !!cartEntity;
  const quantity = cartEntity?.quantity || 0;
  const yaSplitInfo = useSelector(getYaSplitInfo);

  const { isSticky } = useStickyLogic(containerRef);
  useAddBodyPadding(menuMobileRef, isSticky);

  return (
    <>
      <div className={unfixedOuterContainerStyleStyle} ref={containerRef}>
        {productDataIsLoading ? (
          <StickyCardPanelSkeleton />
        ) : (
          <>
            {isSellable && (
              <div className={innerContainer}>
                {shouldShowTintService && <PdpSelectTintColor />}
                <div data-qa="prices_mf-pdp" className={styles.prices}>
                  <PdpPriceView
                    isStickyPanel={false}
                    className={cn({
                      [styles.whenDataIsLoading.common]: productDataIsLoading,
                    })}
                    inBasket={inBasket}
                    quantity={quantity}
                  />
                </div>
                <Basket additionalMargin={outOfStockPriceDisplayed ? true : false}>
                  <ToCart data-qa="to-cart_mf-pdp">
                    <PdpAddToBasket
                      size="l"
                      classnameCounter={styles.counter}
                      isStickyPanel={false}
                      className={jcm({
                        [styles.whenDataIsLoading.noninteractive]: productDataIsLoading,
                      })}
                    />
                  </ToCart>
                </Basket>
              </div>
            )}
          </>
        )}
      </div>
      {yaSplitInfo && (
        <YandexSplitBanner
          showCatchPushPromo={showCatchPushPromo}
          yaSplitInfo={yaSplitInfo}
          price={price}
          quantity={quantity}
        />
      )}
      {showCatchPushPromo && (
        <div className={pushPromoBanner}>
          <PushPromoBanner
            productName={productName}
            productId={productId}
            productPrice={price}
            productPrevPrice={prevPrice}
            productTag={priceCategory}
            productDiscount={discountPercent}
          />
        </div>
      )}
      {isSticky && !productDataIsLoading && (
        <div ref={menuMobileRef} className={fixedOuterContainerStyle}>
          <div data-qa="photo_thumbnail_mf-pdp" className={thumbImg}>
            <picture data-qa="picture-thumbnail-sticky-element">
              <source media={mediaMobile} srcSet={`${img?.xx} 2x, ${img?.x} 1x`} />
              <ImagePlaceholder
                src={img?.x || ''}
                srcSet={`${img?.xx} 2x, ${img?.x} 1x`}
                alt={productName}
                title={productName}
                className={thumbImg}
                loading="lazy"
                fallbackWidth={40}
                fallbackHeight={40}
                fallbackAlt={t('ui.image.alt')}
              />
            </picture>
          </div>
          <div className={cx(innerContainer, innerContainerFixed)}>
            <div data-qa="prices_mf-pdp-sticky">
              <PdpPriceView
                isStickyPanel={true}
                className={cn(styles.prices, {
                  [styles.whenDataIsLoading.common]: productDataIsLoading,
                })}
                inBasket={inBasket}
                quantity={quantity}
              />
            </div>
            <Basket additionalMargin={true}>
              <ToCart data-qa="to-cart_mf-pdp-sticky">
                <PdpAddToBasket
                  size="l"
                  classnameCounter={counterSticky}
                  isStickyPanel={true}
                  className={jcm({
                    [styles.whenDataIsLoading.noninteractive]: productDataIsLoading,
                  })}
                />
              </ToCart>
            </Basket>
          </div>
        </div>
      )}
    </>
  );
};
